import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { StatusMessage } from 'app/common/templates-next/page-template'
import toFixedValue from 'app/common/lib/toFixedValue'
import { AcquiringSettings, RefillType } from 'app/dataTypes/acquiring/settings'
import { useCurrencySign } from 'app/common/components-next/currency'

import RefillItem from './RefillItem'

type Props = {
  payForAlerts?: boolean
  cafeteriaOwnAccountRefillDisable?: boolean
  acquiringSettings: AcquiringSettings
  hideReplenishChild?: boolean
  hideReplenishChildCamp?: boolean
  onClickRefillChild: (type: RefillType) => void
  onClickRefillSelf: (type: RefillType) => void
}

const Step0 = ({
  payForAlerts,
  cafeteriaOwnAccountRefillDisable,
  acquiringSettings,
  hideReplenishChild,
  hideReplenishChildCamp,
  onClickRefillChild,
  onClickRefillSelf,
}: Props) => {
  const { t } = useTranslation('finances')
  const currency = useCurrencySign('spaceBefore')

  const owner = Array.from(acquiringSettings.cafeteria.values()).find(
    ({ isParentAndAccountOwner }) => isParentAndAccountOwner,
  )

  const handleRefillSelfCafe = useCallback(() => {
    onClickRefillSelf(RefillType.Cafeteria)
  }, [onClickRefillSelf])
  const handleRefillSelfPrimary = useCallback(() => {
    onClickRefillSelf(RefillType.Primary)
  }, [onClickRefillSelf])

  const handleRefillChild = useCallback(() => {
    onClickRefillChild(RefillType.Cafeteria)
  }, [onClickRefillChild])
  const handleRefillChildCamp = useCallback(() => {
    onClickRefillChild(RefillType.Camp)
  }, [onClickRefillChild])

  const empty = !(
    (!cafeteriaOwnAccountRefillDisable && owner?.enabled) ||
    acquiringSettings.availableRefillTypes.includes(RefillType.Primary) ||
    acquiringSettings.availableRefillTypes.includes(RefillType.Cafeteria) ||
    acquiringSettings.availableRefillTypes.includes(RefillType.Camp)
  )

  if (empty) {
    return <StatusMessage skipPadding>{'Отсутствует возможность пополнения'}</StatusMessage>
  }

  return (
    <>
      {/* Свой счет за питание */}
      {!cafeteriaOwnAccountRefillDisable && owner?.enabled && (
        <RefillItem
          header={t('replenish_text', 'Пополнить свой счет')}
          content={t('replenish_cafeteria_text', 'За питание в столовой')}
          comment={
            <span>
              {t('available_text')}
              &nbsp;
              {toFixedValue(owner?.balance)}
              {currency}
            </span>
          }
          onRefillClick={handleRefillSelfCafe}
          outline
        />
      )}

      {/* Счет за выпуск карт и обслуживание */}
      {acquiringSettings.availableRefillTypes.includes(RefillType.Primary) &&
        Array.from(acquiringSettings.primary.values()).map(({ id, balance }) => (
          <RefillItem
            key={id}
            header={
              payForAlerts
                ? t('replenish_text_alt', 'Пополнить мой счет за оповещения')
                : t('replenish_text', 'Пополнить свой счет')
            }
            content={t('replenish_comment_text', 'За выпуск карт и обслуживание')}
            comment={
              <span>
                {t('available_text', 'Доступно')}
                &nbsp;
                {toFixedValue(balance)}
                {currency}
              </span>
            }
            onRefillClick={handleRefillSelfPrimary}
            withWarning={payForAlerts}
            warningText={
              t(
                'pay_description_text',
                'Внимание! Данный платеж предназначен для оплаты сервиса уведомлений. Для оплаты питания воспользуйтесь реквизитами комбината питания',
              ) as string
            }
            warningBtText={t('confirm_bt_text', 'Оплатить за сервис уведомлений') as string}
            outline
          />
        ))}

      {/* Счет детей за питание */}
      {acquiringSettings.availableRefillTypes.includes(RefillType.Cafeteria) &&
        !hideReplenishChild && (
          <RefillItem
            header={t('replenish_child_text', 'Пополнить счет ребенка за питание в школе')}
            onRefillClick={handleRefillChild}
            outline
          />
        )}

      {/* Счет детей за питание в лагере */}
      {acquiringSettings.availableRefillTypes.includes(RefillType.Camp) &&
        !hideReplenishChildCamp && (
          <RefillItem
            header={t(
              'replenish_child_camp_text',
              'Пополнить счет ребенка за питание в пришкольном лагере',
            )}
            onRefillClick={handleRefillChildCamp}
            outline
          />
        )}
    </>
  )
}

export default Step0
